<template>
  <APageHead
    title="문의 관리"
    sub="사용자 문의 내역을 관리하고 답변합니다"
    icon="contact_support"
  >
    <template #actions>
      <ABtn kind="primary" icon="add" @click="openCreateModal">
        문의하기
      </ABtn>
    </template>
  </APageHead>

  <div class="hdi-page">
    <!-- Stats 4종 -->
    <div class="hdi-stats">
      <AStat label="전체 문의" :value="inquiries.length" icon="mail_outline" hint="등록됨" />
      <AStat label="대기 중" :value="pendingCount" icon="hourglass_empty" hint="처리 필요" :accent="pendingCount > 0" />
      <AStat label="처리 중" :value="processingCount" icon="pending_actions" hint="진행 중" />
      <AStat label="답변 완료" :value="completedCount" icon="task_alt" hint="완료" />
    </div>

    <!-- 전화 문의 안내 -->
    <ACard :padding="0">
      <div class="hdi-contact">
        <div class="hdi-contact__icon">
          <i class="material-icons-round">support_agent</i>
        </div>

        <div class="hdi-contact__main">
          <h3 class="hdi-contact__title">{{ $t('helpdesk_contact.title') }}</h3>
          <p class="hdi-contact__desc">{{ $t('helpdesk_contact.desc') }}</p>

          <dl class="hdi-contact__rows">
            <div class="hdi-contact__row">
              <dt class="hdi-contact__label">
                <i class="material-icons-round">badge</i>
                {{ $t('helpdesk_contact.manager_label') }}
              </dt>
              <dd class="hdi-contact__value">{{ $t('helpdesk_contact.manager') }}</dd>
            </div>
            <div class="hdi-contact__row">
              <dt class="hdi-contact__label">
                <i class="material-icons-round">call</i>
                {{ $t('helpdesk_contact.phone_label') }}
              </dt>
              <dd class="hdi-contact__value">
                <a :href="contactTelHref" class="hdi-contact__tel">{{ $t('helpdesk_contact.phone') }}</a>
              </dd>
            </div>
            <div class="hdi-contact__row">
              <dt class="hdi-contact__label">
                <i class="material-icons-round">schedule</i>
                {{ $t('helpdesk_contact.hours_label') }}
              </dt>
              <dd class="hdi-contact__value">{{ $t('helpdesk_contact.hours') }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </ACard>

    <!-- Main Content Card -->
    <ACard :padding="0">
      <template #head>
        <h3 class="hdi-card-title">문의 목록</h3>
        <p class="hdi-card-sub">검색 / 필터 / 정렬 적용 가능</p>
      </template>

      <!-- Filter row -->
      <div class="hdi-filter">
        <div class="hdi-filter__field hdi-filter__field--wide">
          <div class="p-input-group">
            <i class="material-icons-round hdi-filter__icon">search</i>
            <input
              type="text"
              class="p-input"
              placeholder="제목, 내용, 이름 검색..."
              v-model="searchQuery"
              @input="filterInquiries"
            />
          </div>
        </div>
        <div class="hdi-filter__field">
          <div class="p-input-group">
            <i class="material-icons-round hdi-filter__icon">category</i>
            <select class="p-input" v-model="selectedCategory" @change="filterInquiries">
              <option value="">전체 카테고리</option>
              <option v-for="category in categories" :key="category.value" :value="category.value">
                {{ $t(category.labelKey) }}
              </option>
            </select>
          </div>
        </div>
        <div class="hdi-filter__field">
          <div class="p-input-group">
            <i class="material-icons-round hdi-filter__icon">flag</i>
            <select class="p-input" v-model="statusFilter" @change="filterInquiries">
              <option value="">전체 상태</option>
              <option value="대기">대기</option>
              <option value="처리중">처리중</option>
              <option value="답변완료">답변완료</option>
            </select>
          </div>
        </div>
        <div class="hdi-filter__field">
          <div class="p-input-group">
            <i class="material-icons-round hdi-filter__icon">priority_high</i>
            <select class="p-input" v-model="priorityFilter" @change="filterInquiries">
              <option value="">전체 우선순위</option>
              <option value="high">높음</option>
              <option value="normal">보통</option>
              <option value="low">낮음</option>
            </select>
          </div>
        </div>
        <div class="hdi-filter__field">
          <div class="p-input-group">
            <i class="material-icons-round hdi-filter__icon">sort</i>
            <select class="p-input" v-model="sortBy" @change="filterInquiries">
              <option value="latest">최신순</option>
              <option value="oldest">오래된순</option>
              <option value="priority">우선순위순</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Inquiry List -->
      <div class="hdi-body">
        <div v-if="loading" class="hdi-loading">
          <div class="p-spinner" />
          <p class="hdi-loading-text">로딩 중...</p>
        </div>

        <AEmpty v-else-if="filteredInquiries.length === 0" title="검색 결과가 없습니다" />

        <ul v-else class="hdi-list">
          <li
            v-for="inquiry in paginatedInquiries"
            :key="inquiry.id"
            class="hdi-item"
            :class="{ 'is-urgent': inquiry.priority === 'high' || inquiry.priority === 'urgent' }"
            @click="viewInquiry(inquiry)"
          >
            <div class="hdi-item__badges">
              <APill kind="info">{{ getCategoryLabel(inquiry.category) }}</APill>
              <APill :kind="statusPillKind(inquiry.status)">{{ inquiry.status }}</APill>
              <APill :kind="priorityPillKind(inquiry.priority)">{{ getPriorityLabel(inquiry.priority) }}</APill>
            </div>
            <h4 class="hdi-item__title">{{ inquiry.subject }}</h4>
            <p class="hdi-item__content">{{ inquiry.content }}</p>
            <div class="hdi-item__meta">
              <span class="hdi-item__meta-row">
                <i class="material-icons-round">person</i>
                {{ inquiry.user_name }}
              </span>
              <span class="hdi-item__meta-row">
                <i class="material-icons-round">email</i>
                {{ inquiry.user_email }}
              </span>
              <span class="hdi-item__meta-row">
                <i class="material-icons-round">schedule</i>
                {{ formatDate(inquiry.created_at) }}
              </span>
            </div>
          </li>
        </ul>
      </div>

      <!-- Pagination -->
      <div v-if="filteredInquiries.length > 0" class="hdi-foot">
        <div class="hdi-foot__info">
          총 <strong>{{ filteredInquiries.length }}</strong>건 ·
          <span class="hdi-foot__page">{{ currentPage }} / {{ Math.max(totalPages, 1) }} 페이지</span>
        </div>
        <Pagination
          v-if="totalPages > 1"
          v-model:current-page="currentPage"
          :total-pages="totalPages"
        />
      </div>
    </ACard>

    <!-- Detail Modal -->
    <AModal
      v-model="showDetailModal"
      title="문의 상세보기"
      :sub="selectedInquiry ? `${selectedInquiry.user_name}님의 문의` : ''"
      icon="visibility"
      size="lg"
    >
      <template v-if="selectedInquiry">
        <div class="hdi-detail">
          <div class="hdi-detail__head">
            <div class="hdi-item__badges">
              <APill kind="info">{{ getCategoryLabel(selectedInquiry.category) }}</APill>
              <APill :kind="statusPillKind(selectedInquiry.status)">{{ selectedInquiry.status }}</APill>
              <APill :kind="priorityPillKind(selectedInquiry.priority)">{{ getPriorityLabel(selectedInquiry.priority) }}</APill>
            </div>
            <h4 class="hdi-detail__title">{{ selectedInquiry.subject }}</h4>
            <div class="hdi-detail__meta">
              <span class="hdi-item__meta-row">
                <i class="material-icons-round">person</i>
                {{ selectedInquiry.user_name }}
              </span>
              <span class="hdi-item__meta-row">
                <i class="material-icons-round">email</i>
                {{ selectedInquiry.user_email }}
              </span>
              <span class="hdi-item__meta-row">
                <i class="material-icons-round">schedule</i>
                {{ formatDate(selectedInquiry.created_at) }}
              </span>
            </div>
          </div>

          <div class="hdi-detail__content">
            <p class="p-section-label">
              <i class="material-icons-round">description</i>
              문의 내용
            </p>
            <div class="hdi-detail__body">{{ selectedInquiry.content }}</div>
          </div>

          <div
            v-if="selectedInquiry.status === '답변완료' && selectedInquiry.reply"
            class="hdi-detail__reply"
          >
            <p class="p-section-label">
              <i class="material-icons-round">check_circle</i>
              답변 내용
            </p>
            <div class="hdi-reply-box">{{ selectedInquiry.reply }}</div>
            <small class="hdi-reply-date">
              <i class="material-icons-round">schedule</i>
              답변 일시: {{ formatDate(selectedInquiry.reply_date) }}
            </small>
          </div>

          <div v-else class="alert alert-info">
            <i class="material-icons-round">info</i>
            <span>아직 답변이 등록되지 않았습니다. 답변이 등록되면 이메일로 알려드립니다.</span>
          </div>
        </div>
      </template>

      <template #foot>
        <ABtn kind="ghost" icon="close" @click="showDetailModal = false">닫기</ABtn>
      </template>
    </AModal>

    <!-- Create Inquiry Modal -->
    <AModal
      v-model="showCreateModal"
      title="문의하기"
      sub="궁금하신 사항을 문의해 주세요"
      icon="contact_support"
      size="lg"
    >
      <form id="hdi-create-form" @submit.prevent="submitInquiry">
        <div class="hdi-contact-note">
          <i class="material-icons-round">phone_in_talk</i>
          <span>
            {{ $t('helpdesk_contact.modal_note') }}
            <a :href="contactTelHref" class="hdi-contact__tel">{{ $t('helpdesk_contact.phone') }}</a>
            <span class="hdi-contact-note__hours">{{ $t('helpdesk_contact.hours') }}</span>
          </span>
        </div>

        <div class="hdi-form-grid">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">category</i>
              카테고리 <span class="p-field__req">*</span>
            </label>
            <div class="p-input-group">
              <i class="material-icons-round hdi-filter__icon">label</i>
              <select v-model="createForm.category" class="p-input" required>
                <option value="">카테고리를 선택하세요</option>
                <option v-for="category in categories" :key="category.value" :value="category.value">
                  {{ $t(category.labelKey) }}
                </option>
              </select>
            </div>
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">priority_high</i>
              우선순위 <span class="p-field__req">*</span>
            </label>
            <div class="p-input-group">
              <i class="material-icons-round hdi-filter__icon">flag</i>
              <select v-model="createForm.priority" class="p-input" required>
                <option value="low">낮음</option>
                <option value="medium">보통</option>
                <option value="high">높음</option>
                <option value="urgent">긴급</option>
              </select>
            </div>
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">title</i>
            제목 <span class="p-field__req">*</span>
          </label>
          <input
            v-model="createForm.subject"
            type="text"
            class="p-input"
            placeholder="문의 제목을 입력하세요"
            required
          />
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">description</i>
            내용 <span class="p-field__req">*</span>
          </label>
          <textarea
            v-model="createForm.content"
            class="p-input p-input--area"
            rows="8"
            placeholder="문의 내용을 상세히 입력해주세요"
            required
          ></textarea>
        </div>
      </form>

      <template #foot>
        <ABtn kind="ghost" icon="close" @click="showCreateModal = false">취소</ABtn>
        <ABtn kind="primary" icon="send" type="submit" @click="submitInquiry">문의 등록</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination.vue';
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AModal from '@/components/Pineapple/AModal.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

export default {
  name: "HelpDeskInquiry",
  components: {
    Pagination, APageHead, ACard, AStat, APill, ABtn, AModal, AEmpty,
  },
  data() {
    return {
      contactPhone: "+82-70-8798-7345",
      inquiries: [],
      filteredInquiries: [],
      categories: [
        { value: 'general', labelKey: 'helpdesk_category.general' },
        { value: 'payment', labelKey: 'helpdesk_category.payment' },
        { value: 'technical', labelKey: 'helpdesk_category.technical' },
        { value: 'account', labelKey: 'helpdesk_category.account' },
        { value: 'course', labelKey: 'helpdesk_category.course' },
        { value: 'other', labelKey: 'helpdesk_category.other' },
      ],
      loading: false,
      searchQuery: '',
      selectedCategory: '',
      statusFilter: '',
      priorityFilter: '',
      sortBy: 'latest',
      currentPage: 1,
      itemsPerPage: 10,
      selectedInquiry: null,
      createForm: {
        category: '',
        subject: '',
        content: '',
        priority: 'medium',
        user_name: '',
        user_email: '',
      },
      selectedFiles: [],
      showDetailModal: false,
      showCreateModal: false,
    };
  },
  computed: {
    contactTelHref() {
      return `tel:${this.contactPhone.replace(/[^+\d]/g, "")}`;
    },
    isLoggedIn() {
      return !!this.$store.state.auth.token;
    },
    pendingCount() {
      return this.inquiries.filter(i => i.status === '대기').length;
    },
    processingCount() {
      return this.inquiries.filter(i => i.status === '처리중').length;
    },
    completedCount() {
      return this.inquiries.filter(i => i.status === '답변완료').length;
    },
    totalPages() {
      return Math.ceil(this.filteredInquiries.length / this.itemsPerPage);
    },
    paginatedInquiries() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredInquiries.slice(start, end);
    },
  },
  mounted() {
    this.loadInquiries();
  },
  methods: {
    async loadInquiries() {
      this.loading = true;
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/helpdesk/inquiries',
          loadingBar: true,
        });
        this.inquiries = response.data.inquiries || [];
        this.filterInquiries();
      } catch (error) {
        console.error('문의 로딩 실패:', error);
        this.$swal({
          icon: 'error',
          title: '로딩 실패',
          text: '문의 목록을 불러오는데 실패했습니다.',
        });
      } finally {
        this.loading = false;
      }
    },
    filterInquiries() {
      let filtered = [...this.inquiries];

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(inquiry =>
          inquiry.subject.toLowerCase().includes(query) ||
          inquiry.content.toLowerCase().includes(query) ||
          inquiry.user_name.toLowerCase().includes(query)
        );
      }

      if (this.selectedCategory) {
        filtered = filtered.filter(inquiry => inquiry.category === this.selectedCategory);
      }

      if (this.statusFilter) {
        filtered = filtered.filter(inquiry => inquiry.status === this.statusFilter);
      }

      if (this.priorityFilter) {
        filtered = filtered.filter(inquiry => inquiry.priority === this.priorityFilter);
      }

      if (this.sortBy === 'latest') {
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else if (this.sortBy === 'oldest') {
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      } else if (this.sortBy === 'priority') {
        const priorityOrder = { high: 0, normal: 1, low: 2 };
        filtered.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
      }

      this.filteredInquiries = filtered;
      this.currentPage = 1;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    viewInquiry(inquiry) {
      this.selectedInquiry = inquiry;
      this.showDetailModal = true;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getCategoryLabel(categoryValue) {
      const category = this.categories.find(c => c.value === categoryValue);
      return category ? this.$t(category.labelKey) : categoryValue;
    },
    getPriorityLabel(priority) {
      const priorityMap = {
        low: '낮음',
        medium: '보통',
        high: '높음',
        urgent: '긴급',
      };
      return priorityMap[priority] || priority;
    },
    statusPillKind(status) {
      const map = {
        '대기': 'default',
        '처리중': 'info',
        '답변완료': 'ok',
      };
      return map[status] || 'default';
    },
    priorityPillKind(priority) {
      const map = {
        urgent: 'err',
        high: 'err',
        medium: 'warn',
        normal: 'warn',
        low: 'default',
      };
      return map[priority] || 'default';
    },
    openCreateModal() {
      const userInfo = this.$store.state.auth.user_info || {};
      this.createForm = {
        category: '',
        subject: '',
        content: '',
        priority: 'medium',
        user_name: this.isLoggedIn ? (userInfo.name || '') : '',
        user_email: this.isLoggedIn ? (userInfo.email || '') : '',
      };
      this.selectedFiles = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      this.showCreateModal = true;
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files);
      const maxSize = 10 * 1024 * 1024;
      const validFiles = [];
      for (const file of files) {
        if (file.size > maxSize) {
          this.$swal({
            icon: 'error',
            title: '파일 크기 초과',
            text: `${file.name}: 최대 10MB까지 가능합니다`,
          });
          continue;
        }
        validFiles.push(file);
      }
      this.selectedFiles = [...this.selectedFiles, ...validFiles];
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
      if (this.selectedFiles.length === 0 && this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    async submitInquiry() {
      try {
        const formData = new FormData();
        formData.append('category', this.createForm.category);
        formData.append('subject', this.createForm.subject);
        formData.append('content', this.createForm.content);
        formData.append('priority', this.createForm.priority);

        if (!this.isLoggedIn) {
          formData.append('user_name', this.createForm.user_name);
          formData.append('user_email', this.createForm.user_email);
        }

        for (const file of this.selectedFiles) {
          formData.append('attachments', file);
        }

        const config = {
          method: 'post',
          url: '/helpdesk/inquiries',
          data: formData,
          loadingBar: true,
        };

        if (this.isLoggedIn) {
          config.headers = {
            Authorization: `Bearer ${this.$store.state.auth.token}`,
          };
        }

        await this.$axios(config);

        this.$swal({
          icon: 'success',
          title: '문의 등록 완료',
          text: '문의가 성공적으로 등록되었습니다',
        });

        this.showCreateModal = false;
        this.loadInquiries();
      } catch (error) {
        console.error('문의 등록 실패:', error);
        this.$swal({
          icon: 'error',
          title: '등록 실패',
          text: error.response?.data?.message || '문의 등록에 실패했습니다',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.hdi-page {
  padding: 20px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $p-font-body;
}

.hdi-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.hdi-card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $p-ink;
}

.hdi-card-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: $p-ink-mid;
}

// 전화 문의 안내
.hdi-contact {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(180deg, $p-point-soft 0%, $p-panel 78%);
  border-left: 4px solid $p-point;
}

.hdi-contact__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $p-radius;
  background: $p-point;
  border: 1px solid $p-point-deep;

  i { font-size: 24px !important; color: $p-ink; }
}

.hdi-contact__main {
  flex: 1;
  min-width: 0;
}

.hdi-contact__title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $p-ink;
}

.hdi-contact__desc {
  margin: 3px 0 12px;
  font-size: 12px;
  color: $p-ink-mid;
  line-height: 1.5;
}

.hdi-contact__rows {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.hdi-contact__row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.hdi-contact__label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  width: 88px;
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: $p-ink-mid;

  i { font-size: 15px !important; color: $p-ink-soft; }
}

.hdi-contact__value {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
  line-height: 1.5;
  word-break: keep-all;
}

.hdi-contact__tel {
  font-family: $p-font-mono;
  font-weight: 700;
  color: $p-ink;
  text-decoration: none;
  border-bottom: 1px dashed $p-point-deep;

  &:hover { color: $p-point-deep; }
}

// 문의 등록 모달 상단 안내
.hdi-contact-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: $p-radius;
  background: $p-point-soft;
  border: 1px solid rgba(201, 138, 10, 0.25);
  font-size: 12px;
  line-height: 1.6;
  color: $p-ink2;

  i { font-size: 18px !important; color: $p-point-deep; flex-shrink: 0; }
}

.hdi-contact-note__hours {
  color: $p-ink-mid;

  &::before {
    content: "·";
    margin: 0 5px;
    color: $p-ink-soft;
  }
}

// Filter
.hdi-filter {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid $p-line;
}

.hdi-filter__field {
  min-width: 0;
}

.hdi-filter__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px !important;
  color: $p-ink-soft;
  pointer-events: none;
  z-index: 1;
}

:deep(.p-input-group) {
  position: relative;
  display: flex;
  align-items: center;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  transition: border-color 0.15s, box-shadow 0.15s;
}
:deep(.p-input-group:focus-within) {
  border-color: $p-point-deep;
  box-shadow: 0 0 0 3px $p-point-glow;
}
:deep(.p-input-group > .p-input) {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  height: 38px;
  padding: 0 12px 0 38px;
  width: 100%;
}
:deep(select.p-input) {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231F1B14' stroke-width='2.2' stroke-linecap='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  padding-right: 30px !important;
}

// Body / List
.hdi-body {
  padding: 16px 20px;
  min-height: 200px;
}

.hdi-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
}

.p-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid $p-line;
  border-top-color: $p-point;
  border-radius: 50%;
  animation: hdi-spin 0.7s linear infinite;
}
@keyframes hdi-spin {
  to { transform: rotate(360deg); }
}

.hdi-loading-text {
  color: $p-ink-mid;
  font-size: 13px;
}

.hdi-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hdi-item {
  padding: 14px 16px;
  border: 1px solid $p-line;
  border-radius: $p-radius;
  background: $p-panel;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;

  &:hover {
    background: $p-panel-alt;
    border-color: $p-line-hard;
    box-shadow: 0 2px 8px rgba(31, 27, 20, 0.06);
  }

  &.is-urgent {
    border-left: 4px solid $p-err;
  }
}

.hdi-item__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.hdi-item__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 800;
  color: $p-ink;
}

.hdi-item__content {
  margin: 0 0 10px;
  font-size: 13px;
  color: $p-ink-mid;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hdi-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: $p-ink-soft;
}

.hdi-item__meta-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  i { font-size: 14px !important; color: $p-ink-soft; }
}

// Foot
.hdi-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px 16px;
  border-top: 1px solid $p-line;
  flex-wrap: wrap;
}

.hdi-foot__info {
  font-size: 12px;
  color: $p-ink-mid;

  strong {
    font-family: $p-font-mono;
    color: $p-ink;
    font-weight: 800;
  }
}

.hdi-foot__page {
  font-family: $p-font-mono;
  color: $p-ink2;
  font-weight: 700;
  margin-left: 4px;
}

// Detail Modal
.hdi-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hdi-detail__head {
  padding: 14px 16px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: $p-radius;
}

.hdi-detail__title {
  margin: 8px 0;
  font-size: 16px;
  font-weight: 800;
  color: $p-ink;
}

.hdi-detail__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: $p-ink-soft;
}

.hdi-detail__content,
.hdi-detail__reply {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hdi-detail__body {
  padding: 14px 16px;
  background: $p-panel-alt;
  border: 1px solid $p-line-soft;
  border-radius: $p-radius;
  font-size: 13px;
  line-height: 1.6;
  color: $p-ink2;
  white-space: pre-line;
}

.hdi-reply-box {
  padding: 14px 16px;
  background: $p-ok-soft;
  border: 1px solid rgba(30, 140, 102, 0.25);
  border-left: 4px solid $p-ok;
  border-radius: $p-radius;
  font-size: 13px;
  line-height: 1.6;
  color: $p-ink;
  white-space: pre-line;
}

.hdi-reply-date {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: $p-ink-soft;

  i { font-size: 13px !important; }
}

.alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border-radius: $p-radius;
  font-size: 13px;
  line-height: 1.5;

  i { font-size: 20px !important; flex-shrink: 0; }
}

.alert-info {
  background: $p-info-soft;
  border: 1px solid rgba(59, 123, 196, 0.25);
  color: $p-ink2;

  i { color: $p-info; }
}

// Create Modal form
.hdi-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

@media (max-width: 1100px) {
  .hdi-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hdi-filter { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 640px) {
  .hdi-stats { grid-template-columns: 1fr; }
  .hdi-contact__label { width: 100%; }
  .hdi-filter { grid-template-columns: 1fr; }
  .hdi-form-grid { grid-template-columns: 1fr; }
}
</style>
